import type { QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { sentryLog } from "@/utils/sentry-utils";

const WARN_MS = 5_000;
const STUCK_MS = 20_000;

type Timers = {
  startedAt: number;
  label: string;
  warn?: ReturnType<typeof setTimeout>;
  stuck: ReturnType<typeof setTimeout>;
};

function context(extra: Record<string, unknown>) {
  return {
    ...extra,
    route: window.location.pathname,
    online: navigator.onLine,
    visibility: document.visibilityState,
  };
}

function clearTimers(map: Map<string | number, Timers>, key: string | number) {
  const timers = map.get(key);
  if (!timers) return;
  if (timers.warn) clearTimeout(timers.warn);
  clearTimeout(timers.stuck);
  map.delete(key);
}

/**
 * Reports React Query loaders that stay pending past a threshold to Sentry.
 * Server logs only record requests that finish, so a request hung against an
 * unresponsive backend is invisible there — this catches it from the client.
 */
export function installLoaderWatchdog(queryClient: QueryClient) {
  const queryTimers = new Map<string | number, Timers>();
  const mutationTimers = new Map<string | number, Timers>();

  queryClient.getQueryCache().subscribe((event) => {
    const { query } = event;
    const key = query.queryHash;

    if (event.type === "removed") {
      clearTimers(queryTimers, key);
      return;
    }

    if (query.state.fetchStatus !== "fetching") {
      clearTimers(queryTimers, key);
      return;
    }

    if (queryTimers.has(key)) return;

    const startedAt = Date.now();
    const queryKey = JSON.stringify(query.queryKey);
    const stillFetching = () =>
      queryClient.getQueryCache().get(key)?.state.fetchStatus === "fetching";

    queryTimers.set(key, {
      startedAt,
      label: queryKey,
      warn: setTimeout(() => {
        if (!stillFetching()) return;
        sentryLog.warn("Loader slow: query pending >5s", context({ queryKey }));
      }, WARN_MS),
      stuck: setTimeout(() => {
        if (!stillFetching()) return;
        sentryLog.error(
          "Loader stuck: query pending >20s",
          context({ queryKey, elapsedMs: Date.now() - startedAt }),
        );
      }, STUCK_MS),
    });
  });

  // Mutations (e.g. invoice fiscalization) legitimately run several seconds, so
  // only the >20s "stuck" tier is reported — a 5s warn would be noise.
  queryClient.getMutationCache().subscribe((event) => {
    const { mutation } = event;
    if (!mutation) return;
    const key = mutation.mutationId;

    if (event.type === "removed" || mutation.state.status !== "pending") {
      clearTimers(mutationTimers, key);
      return;
    }

    if (mutationTimers.has(key)) return;

    const startedAt = Date.now();
    const mutationKey = mutation.options.mutationKey
      ? JSON.stringify(mutation.options.mutationKey)
      : "(unkeyed)";

    mutationTimers.set(key, {
      startedAt,
      label: mutationKey,
      stuck: setTimeout(() => {
        const live = queryClient
          .getMutationCache()
          .getAll()
          .find((m) => m.mutationId === key);
        if (live?.state.status !== "pending") return;
        sentryLog.error(
          "Loader stuck: mutation pending >20s",
          context({ mutationKey, elapsedMs: Date.now() - startedAt }),
        );
      }, STUCK_MS),
    });
  });

  // The timers above live in the page's JS context, so a user who refreshes or
  // closes the tab before they fire produces no report. Catch that on the way
  // out: report any loader still pending past the warn threshold right then.
  window.addEventListener("pagehide", () => {
    const now = Date.now();
    const collect = (
      kind: "query" | "mutation",
      map: Map<string | number, Timers>,
    ) =>
      [...map.values()].flatMap((t) => {
        const elapsedMs = now - t.startedAt;
        return elapsedMs >= WARN_MS
          ? [{ kind, loader: t.label, elapsedMs }]
          : [];
      });

    const pending = [
      ...collect("query", queryTimers),
      ...collect("mutation", mutationTimers),
    ];
    if (pending.length === 0) return;

    Sentry.captureMessage("Loader abandoned: tab closed with pending loader", {
      level: "warning",
      extra: context({ pending }),
    });
    void Sentry.flush(2000);
  });
}
